/**
 * Generated by orval v8.11.0 🍺
 * Do not edit manually.
 * Core API
 * Core API Documentation
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  CreateFireExtinguisherCheckDto,
  CreateFireExtinguisherCheckResponseDto,
  FireExtinguisherCheckDetailDto,
  ListFireExtinguisherChecksQueryControllerListParams,
  ListFireExtinguisherChecksResponseDto,
} from "../model";

import { axiosClient } from "../../lib/api";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Creates an immutable inspection record covering one or more registered extinguishers, each with its six visual pass/fail checks and an optional comment. The extinguisher name and room are snapshotted at submit time.
 * @summary Submit a monthly fire extinguisher check
 */
export const createFireExtinguisherCheckCommandControllerCreate = (
  nurseryId: string,
  createFireExtinguisherCheckDto: CreateFireExtinguisherCheckDto,
  signal?: AbortSignal,
) => {
  return axiosClient<CreateFireExtinguisherCheckResponseDto>({
    url: `/v1/nurseries/${nurseryId}/fire-extinguisher-checks`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: createFireExtinguisherCheckDto,
    signal,
  });
};

export const getCreateFireExtinguisherCheckCommandControllerCreateMutationOptions =
  <TError = void, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
      >,
      TError,
      { nurseryId: string; data: CreateFireExtinguisherCheckDto },
      TContext
    >;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
    >,
    TError,
    { nurseryId: string; data: CreateFireExtinguisherCheckDto },
    TContext
  > => {
    const mutationKey = ["createFireExtinguisherCheckCommandControllerCreate"];
    const { mutation: mutationOptions } = options
      ? options.mutation &&
        "mutationKey" in options.mutation &&
        options.mutation.mutationKey
        ? options
        : { ...options, mutation: { ...options.mutation, mutationKey } }
      : { mutation: { mutationKey } };

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
      >,
      { nurseryId: string; data: CreateFireExtinguisherCheckDto }
    > = (props) => {
      const { nurseryId, data } = props ?? {};

      return createFireExtinguisherCheckCommandControllerCreate(
        nurseryId,
        data,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type CreateFireExtinguisherCheckCommandControllerCreateMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
    >
  >;
export type CreateFireExtinguisherCheckCommandControllerCreateMutationBody =
  CreateFireExtinguisherCheckDto;
export type CreateFireExtinguisherCheckCommandControllerCreateMutationError =
  void;

/**
 * @summary Submit a monthly fire extinguisher check
 */
export const useCreateFireExtinguisherCheckCommandControllerCreate = <
  TError = void,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
      >,
      TError,
      { nurseryId: string; data: CreateFireExtinguisherCheckDto },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<typeof createFireExtinguisherCheckCommandControllerCreate>
  >,
  TError,
  { nurseryId: string; data: CreateFireExtinguisherCheckDto },
  TContext
> => {
  return useMutation(
    getCreateFireExtinguisherCheckCommandControllerCreateMutationOptions(
      options,
    ),
    queryClient,
  );
};
/**
 * Paginated, chronological list of monthly fire extinguisher checks, filterable by submission date range. Visible to managers + deputy managers.
 * @summary List fire extinguisher checks for a nursery (manager view)
 */
export const listFireExtinguisherChecksQueryControllerList = (
  nurseryId: string,
  params?: ListFireExtinguisherChecksQueryControllerListParams,
  signal?: AbortSignal,
) => {
  return axiosClient<ListFireExtinguisherChecksResponseDto>({
    url: `/v1/nurseries/${nurseryId}/fire-extinguisher-checks`,
    method: "GET",
    params,
    signal,
  });
};

export const getListFireExtinguisherChecksQueryControllerListQueryKey = (
  nurseryId: string,
  params?: ListFireExtinguisherChecksQueryControllerListParams,
) => {
  return [
    `/v1/nurseries/${nurseryId}/fire-extinguisher-checks`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListFireExtinguisherChecksQueryControllerListQueryOptions = <
  TData = Awaited<
    ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListFireExtinguisherChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListFireExtinguisherChecksQueryControllerListQueryKey(nurseryId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listFireExtinguisherChecksQueryControllerList>>
  > = ({ signal }) =>
    listFireExtinguisherChecksQueryControllerList(nurseryId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: nurseryId !== null && nurseryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listFireExtinguisherChecksQueryControllerList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListFireExtinguisherChecksQueryControllerListQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof listFireExtinguisherChecksQueryControllerList>>
  >;
export type ListFireExtinguisherChecksQueryControllerListQueryError = void;

export function useListFireExtinguisherChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params: undefined | ListFireExtinguisherChecksQueryControllerListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
          >,
          TError,
          Awaited<
            ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListFireExtinguisherChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListFireExtinguisherChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
          >,
          TError,
          Awaited<
            ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
          >
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListFireExtinguisherChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListFireExtinguisherChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List fire extinguisher checks for a nursery (manager view)
 */

export function useListFireExtinguisherChecksQueryControllerList<
  TData = Awaited<
    ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
  >,
  TError = void,
>(
  nurseryId: string,
  params?: ListFireExtinguisherChecksQueryControllerListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<typeof listFireExtinguisherChecksQueryControllerList>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getListFireExtinguisherChecksQueryControllerListQueryOptions(
      nurseryId,
      params,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * @summary Get a fire extinguisher check with its full per-extinguisher breakdown
 */
export const getFireExtinguisherCheckQueryControllerGet = (
  fireExtinguisherCheckId: string,
  signal?: AbortSignal,
) => {
  return axiosClient<FireExtinguisherCheckDetailDto>({
    url: `/v1/fire-extinguisher-checks/${fireExtinguisherCheckId}`,
    method: "GET",
    signal,
  });
};

export const getGetFireExtinguisherCheckQueryControllerGetQueryKey = (
  fireExtinguisherCheckId: string,
) => {
  return [`/v1/fire-extinguisher-checks/${fireExtinguisherCheckId}`] as const;
};

export const getGetFireExtinguisherCheckQueryControllerGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
  >,
  TError = void,
>(
  fireExtinguisherCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFireExtinguisherCheckQueryControllerGetQueryKey(
      fireExtinguisherCheckId,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>
  > = ({ signal }) =>
    getFireExtinguisherCheckQueryControllerGet(fireExtinguisherCheckId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      fireExtinguisherCheckId !== null && fireExtinguisherCheckId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetFireExtinguisherCheckQueryControllerGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>
>;
export type GetFireExtinguisherCheckQueryControllerGetQueryError = void;

export function useGetFireExtinguisherCheckQueryControllerGet<
  TData = Awaited<
    ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
  >,
  TError = void,
>(
  fireExtinguisherCheckId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
          >,
          TError,
          Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetFireExtinguisherCheckQueryControllerGet<
  TData = Awaited<
    ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
  >,
  TError = void,
>(
  fireExtinguisherCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
          >,
          TError,
          Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>
        >,
        "initialData"
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetFireExtinguisherCheckQueryControllerGet<
  TData = Awaited<
    ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
  >,
  TError = void,
>(
  fireExtinguisherCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get a fire extinguisher check with its full per-extinguisher breakdown
 */

export function useGetFireExtinguisherCheckQueryControllerGet<
  TData = Awaited<
    ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>
  >,
  TError = void,
>(
  fireExtinguisherCheckId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFireExtinguisherCheckQueryControllerGet>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions =
    getGetFireExtinguisherCheckQueryControllerGetQueryOptions(
      fireExtinguisherCheckId,
      options,
    );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}
